import axios from 'axios';
import { AuthService } from './AuthService';
import { AzureAuthData } from 'types/Azure';
import { BASE_URL } from 'config/api';


  // Azure Auth endpoint is at /auth/azure/callback (not /api/auth/azure/callback)
const AZURE_BASE_URL = BASE_URL;

class AzureAuthService {
  private static instance: AzureAuthService;
  private azureAuthData: AzureAuthData | undefined = undefined;
  private azureAuthCheckPromise: Promise<AzureAuthData> | null = null;

  private constructor() {}

  public static getInstance(): AzureAuthService {
    if (!AzureAuthService.instance) {
      AzureAuthService.instance = new AzureAuthService();
    }
    return AzureAuthService.instance;
  }

  public async checkAzureAuth(): Promise<AzureAuthData> {
    // If we already have a health check in progress, return that promise
    if (this.azureAuthCheckPromise) {
      return this.azureAuthCheckPromise;
    }

    // If we already have cached health data, return it immediately
    if (this.azureAuthData) {
      return Promise.resolve(this.azureAuthData);
    }

    // Start a new health check
    this.azureAuthCheckPromise = this.performAzureAuthCheck();
    
    try {
      const result = await this.azureAuthCheckPromise;
      this.azureAuthData = result;
      return result;
    } finally {
      this.azureAuthCheckPromise = null;
    }
  }

 private async performAzureAuthCheck(): Promise<AzureAuthData> {
    try {
      console.log('🏥 [AZURE AUTH] Checking Azure authentication...');
      
      const response = await axios.get(`${AZURE_BASE_URL}/azure-auth`, {
        headers: {
          'accept': 'application/json'
        },
        timeout: 10000 // 10 second timeout
      });

      const azureAuthData: AzureAuthData = response.data;
      
      console.log('✅ [AZURE AUTH] Azure authentication successful:', {
        access_token: azureAuthData.access_token,
        token_type: azureAuthData.token_type,
        expires_in: azureAuthData.expires_in
      });

      await AuthService.handleAccessToken(azureAuthData.access_token, 'azure');

      return azureAuthData;
      
    } catch (error: any) {
      console.error('❌ [AZURE AUTH] Azure authentication failed:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url
      });
      
      if (error.response?.status === 403) {
        return {
          access_token: '',
          token_type: '',
          expires_in: 0,
          is_enabled: true
        } as unknown as AzureAuthData;
      }
      return null as unknown as AzureAuthData;
    }
  }

  /**
   * Re-run the exchange to mint a *fresh* token for an already-signed-in user.
   *
   * `/azure-auth` calls `write_token()` on every request, so this is a real
   * renewal path with no backend change — but only while the EasyAuth platform
   * session is alive. Once that cookie is gone the only way back in is
   * `initiateMicrosoftLogin()`, which navigates away and takes unsaved work
   * with it; hence the boolean, so the caller can prompt instead.
   *
   * Deliberately bypasses (and refreshes) the memo that `checkAzureAuth()`
   * relies on. `checkAzureAuth()` itself must stay cached: `AzureAuthProvider`
   * and `LoginForm` use it as a provider probe on every mount.
   */
  public async forceRenew(): Promise<boolean> {
    this.azureAuthData = undefined;
    try {
      const data = await this.performAzureAuthCheck();
      // The 403 branch returns `is_enabled` with an EMPTY access_token to mean
      // "provider is on, but this browser has no local user". That is a
      // failure to renew, however successful the HTTP call looks.
      if (!data?.access_token) return false;
      this.azureAuthData = data;
      return true;
    } catch {
      return false;
    }
  }

  public initiateMicrosoftLogin(): void {
    console.log('🔐 [AZURE AUTH] Initiating Microsoft login...');
    // Redirect to the Azure login endpoint
    const loginUrl = `${AZURE_BASE_URL}/.auth/login/aad?post_login_redirect_uri=/`;
    window.location.href = loginUrl;
  }

  public logoutFromMicrosoft(): void {
    console.log('🚪 [AZURE AUTH] Logging out from Microsoft...'); 
    
    // Clear local state
    this.azureAuthData = undefined;
    
    // Use EasyAuth platform logout so we sign out from all configured providers.
    // EasyAuth docs: `/.auth/logout` clears EasyAuth cookies/tokens and signs out on the provider.
    const logoutUrl = `${AZURE_BASE_URL}/.auth/logout?post_logout_redirect_uri=/`;
    window.location.href = logoutUrl;
  }
}

export default AzureAuthService.getInstance();
